import jsPDF from 'jspdf'
import dayjs from 'dayjs'
import { DENSITIES, GRAY, LIGHT, PAGE, fullName, hexToRgb } from './shared'
import modern from './modern'
import classic from './classic'
import sidebar from './sidebar'

export const LAYOUTS = { modern, classic, sidebar }

export const LAYOUT_OPTIONS = [
  { value: 'modern', label: 'Modern' },
  { value: 'classic', label: 'Classic' },
  { value: 'sidebar', label: 'Sidebar' },
]

// The logo is drawn at most ~170 × 40 pt; twice that in pixels stays sharp when printed
export const LOGO_SIZE = { maxWidth: 360, maxHeight: 90 }

const newDoc = () => new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait', compress: true })

const contextFor = (card, base) => {
  const { t, markingPeriodId } = base
  const periods = [...(card.markingPeriods ?? [])].sort((a, b) => dayjs(a.startDate).diff(dayjs(b.startDate)))
  const selected = periods.find(period => period.id === markingPeriodId) ?? periods[periods.length - 1]
  const attendance = card.attendance ?? {}
  const total = attendance.total ?? {}
  const attended = (Number(total.present) || 0) + (Number(total.absent) || 0)
  const { student } = card

  return {
    ...base,
    periods,
    selected,
    attendance,
    attendanceRate: attended ? Math.round(((Number(total.present) || 0) / attended) * 1000) / 10 : null,
    hasGpa: card.gpa?.cumulative != null || card.gpa?.markingPeriods?.some(item => item.gpa != null),
    subtitle: [t('Report Card'), base.academicYearName, selected && t('Through {{name}}', { name: selected.name })]
      .filter(Boolean)
      .join('  ·  '),
    fields: [
      [t('Grade'), student.gradeLevel?.name ?? student.gradeLevel?.id],
      [t('Section'), student.gradeSection?.name],
      [t('Student ID'), student.localId],
    ].filter(([, value]) => value != null && value !== ''),
  }
}

const fittingDensity = (layout, card, ctx) =>
  DENSITIES.find(d => {
    const probe = newDoc()
    const bottom = layout.render(probe, card, { ...ctx, logo: null }, d)
    return probe.internal.getNumberOfPages() === 1 && bottom <= layout.limit
  }) ?? DENSITIES[DENSITIES.length - 1]

const footer = (doc, name, generated) => {
  doc.setDrawColor(...LIGHT)
  doc.setLineWidth(0.6)
  doc.line(PAGE.margin, PAGE.height - 40, PAGE.width - PAGE.margin, PAGE.height - 40)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(...GRAY)
  doc.text(name, PAGE.margin, PAGE.height - 26)
  doc.text(generated, PAGE.width - PAGE.margin, PAGE.height - 26, { align: 'right' })
}

export function buildReportCardPdf({
  cards,
  layout: layoutName = 'modern',
  markingPeriodId,
  logo,
  schoolName,
  academicYearName,
  primaryColor,
  t: translate,
}) {
  const t = (key, values) => translate(key, { ...values, interpolation: { escapeValue: false } })
  const layout = LAYOUTS[layoutName] ?? modern
  const base = { t, markingPeriodId, logo, schoolName, academicYearName, primary: hexToRgb(primaryColor) }
  const generated = t('Generated {{date}}', { date: dayjs().format('MM/DD/YYYY') })
  const doc = newDoc()

  cards.forEach((card, index) => {
    if (index > 0) doc.addPage()
    const firstPage = doc.internal.getNumberOfPages()
    const ctx = contextFor(card, base)
    layout.render(doc, card, ctx, fittingDensity(layout, card, ctx))

    for (let page = firstPage; page <= doc.internal.getNumberOfPages(); page += 1) {
      doc.setPage(page)
      footer(doc, fullName(card.student), generated)
    }
  })

  doc.setProperties({ title: `${t('Report Card')} · ${schoolName ?? ''}` })
  return doc
}
